// Der Zustandsautomat der Seite: Was ist zu sehen – Anmeldeseite, Spiel oder der Hinweis, dass
// die Sitzung woanders fortgesetzt wurde? Reine Logik ohne DOM, damit sie geprüft werden kann;
// main.ts hängt die Ansichten daran (docs/architektur-paket-2.md, Abschnitt „Client (auth/)“).
//
// Vier Ansichten, und jede hat genau einen Weg hinein:
//   prueft     → beim Laden, solange `GET /api/ich` unterwegs ist (die Seite zeigt nichts)
//   anmeldung  → keine gültige Sitzung, oder abgemeldet
//   spiel      → gültige Sitzung
//   kick       → die Sitzung wurde woanders fortgesetzt; nur „Neu anmelden“ führt weiter
// Ereignisse, die zur Ansicht nicht passen, lassen den Zustand unverändert (und liefern dasselbe
// Objekt zurück, damit ein Vergleich mit === „hat sich nichts geändert“ heißt).
import type { IchDaten, Sitzungsgrund } from './api.js';

export type Ansicht = 'prueft' | 'anmeldung' | 'spiel' | 'kick';

export interface Zustand {
  ansicht: Ansicht;
  /** Kurzer Satz über den Karten der Anmeldeseite bzw. im Kick-Hinweis; null heißt: keiner. */
  hinweis: string | null;
  /** Das eigene Konto, sobald es bekannt ist. */
  ich: IchDaten | null;
}

export type Ereignis =
  /** `GET /api/ich` hat ein Konto geliefert. */
  | { art: 'sitzungDa'; ich: IchDaten }
  /** `GET /api/ich` hat 401 geliefert – mit dem Grund. */
  | { art: 'sitzungFehlt'; grund: Sitzungsgrund }
  /** Ein Formular der Anmeldeseite war erfolgreich. */
  | { art: 'angemeldet'; ich: IchDaten }
  /** Die Nachricht `kicked` kam über die WebSocket-Verbindung. */
  | { art: 'hinausgeworfen' }
  /** „Abmelden“ im Menü ist durch. */
  | { art: 'abgemeldet' }
  /** „Neu anmelden“ im Kick-Hinweis. */
  | { art: 'zurueckZurAnmeldung' };

export const HINWEIS_ANDERSWO = 'Du wurdest woanders angemeldet.';

/** Beim Laden der Seite: erst fragen, dann zeigen. */
export const START: Zustand = { ansicht: 'prueft', hinweis: null, ich: null };

/** Der Satz zu einem Sitzungsgrund; „keine“ braucht keinen – niemand war je angemeldet. */
function hinweisZuGrund(grund: Sitzungsgrund): string | null {
  switch (grund) {
    case 'anderswo':
      return HINWEIS_ANDERSWO;
    case 'abgemeldet':
      return 'Du bist abgemeldet.';
    default:
      return null;
  }
}

function anmeldung(hinweis: string | null = null): Zustand {
  return { ansicht: 'anmeldung', hinweis, ich: null };
}

/** Wendet ein Ereignis auf den Zustand an; unpassende Ereignisse ändern nichts. */
export function naechsterZustand(zustand: Zustand, ereignis: Ereignis): Zustand {
  switch (ereignis.art) {
    case 'sitzungDa':
      if (zustand.ansicht !== 'prueft') return zustand;
      return { ansicht: 'spiel', hinweis: null, ich: ereignis.ich };

    case 'angemeldet':
      if (zustand.ansicht !== 'anmeldung') return zustand;
      return { ansicht: 'spiel', hinweis: null, ich: ereignis.ich };

    case 'sitzungFehlt':
      // Aus dem Spiel heraus heißt „anderswo“ dasselbe wie die Nachricht `kicked`: Der Hinweis
      // kommt so auch dann, wenn die Nachricht selbst verloren ging.
      if (zustand.ansicht === 'spiel' && ereignis.grund === 'anderswo') return hinausgeworfen(zustand);
      if (zustand.ansicht === 'kick') return zustand;
      return anmeldung(hinweisZuGrund(ereignis.grund));

    case 'hinausgeworfen':
      if (zustand.ansicht !== 'spiel') return zustand;
      return hinausgeworfen(zustand);

    case 'abgemeldet':
      if (zustand.ansicht === 'anmeldung') return zustand;
      return anmeldung();

    case 'zurueckZurAnmeldung':
      if (zustand.ansicht !== 'kick') return zustand;
      return anmeldung();
  }
}

/** Der Kick-Hinweis behält das Konto: Der Name kann danach im Formular stehen bleiben. */
function hinausgeworfen(zustand: Zustand): Zustand {
  return { ansicht: 'kick', hinweis: HINWEIS_ANDERSWO, ich: zustand.ich };
}
